<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import OrderRequirements from "@retailer/svelte/Shared/DistributorInfo/OrderRequirements.svelte";
  import CloudinaryImageService from "@shared/svelte/Modules/CloudinaryImageService";
  import Price from "@retailer/svelte/Cart/Page/DistributorSection/Price.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import OrderAlertsSection from "@retailer/svelte/Cart/Page/DistributorSection/OrderAlertsSection.svelte";
  import { CartOrderEntity, SalesRepsEventEntity } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  // Types
  import type { CartOrderType } from "@shared/types/Cart";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import {
    getRetailerUserAuthenticationStatus,
    shouldHideAllDistributorInfo,
    getStockSiteLocation,
  } from "@shared/services/retailer_user_authentication_service";

  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  // Props
  export let isMobile: boolean;
  export let order: CartOrderType;
  export let openConnectDistributorModal: any;
  export let total: number;
  export let products: any;
  export let accordionIcon: string = "ArrowDown";
  export let onClickAccordionCallback: any;
  export let accordionOpenByDefault: boolean;
  export let cartService: any;
  export let highlightErrors: boolean;

  let hoveringForAccordionButton: boolean = false;
  let accordionOpen: boolean = accordionOpenByDefault;
  let tooltipText: string = "Show details";
  let needsAccountNumber;
  let emptyText;

  const { trackClick } = EventTracking;

  // Functions
  const orderSalesRepsString = (order: CartOrderType) => {
    return order.sales_reps.map(rep => rep.name).join(", ");
  };

  const orderSalesRepsIds = (order: CartOrderType) => {
    return order.sales_reps.map(rep => rep.id);
  };

  const onClickAccordion = () => {
    accordionOpen = !accordionOpen;
    onClickAccordionCallback();
  };

  const enterAccordionButton = () => {
    hoveringForAccordionButton = true;
  };

  const leaveAccordionButton = () => {
    hoveringForAccordionButton = false;
  };

  const onOrderReqModalOpen = () => {
    trackClick("cart_order_req_open", [
      new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
    ]);
  };

  $: {
    accordionIcon = !accordionOpen ? "ArrowDown" : "ArrowUp";
    tooltipText = !accordionOpen ? "Show details" : "Hide details";
    needsAccountNumber = !order.distributor.reasons?.account;
    const accountNumberEmptyText =
      order.distributor.id === VT_802_SPIRITS_DISTRIBUTOR_ID
        ? "Account not connected"
        : "No account number added";
    emptyText = needsAccountNumber ? accountNumberEmptyText : "No sales rep added";
  }

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  let hideAllDistributorInfo: boolean = false;
  let retailerAuth;

  $: {
    retailerAuth = $RetailerUserAuthenticationsStore.find(
      a => a.distributor_id == order.distributor.id,
    );
  }

  $: hideAllDistributorInfo =
    hasRetailerUserAuthRollout &&
    order.distributor?.id &&
    shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, [order.distributor.id]);

  $: retailerUserAuthStatus =
    hasRetailerUserAuthRollout &&
    getRetailerUserAuthenticationStatus($RetailerUserAuthenticationsStore, [
      order?.distributor?.id,
    ]);
</script>

<div class="fizz-surface fizz-inset-horizontal-16">
  <section class="fizz-inset-vertical-12">
    {#if order.distributor?.ordering_disabled}
      <div class="fizz-alert-warning fizz-alert--small fizz-stack-12">
        Provi is temporarily not accepting orders for {order.distributor.name}. You can submit this
        order when ordering resumes.
      </div>
    {/if}
    <span class="fizz-flex-row-8-responsive fizz-align-content-start">
      <div class="fizz-flex-row-12 fizz-flex-item fizz-align-content-start">
        <div class="fizz-bordered icon-container fizz-flex-no-shrink fizz-icon-32">
          {#if order.distributor.cloudinary_image_url}
            <img
              on:error={() => (order.distributor.cloudinary_image_url = null)}
              class="logo"
              src={CloudinaryImageService.getUrl(order.distributor.cloudinary_image_url, 75, 75)}
              alt="Logo"
            />
          {:else}
            <Icon type="DistributorLogoPlaceholder" extraStyles="height: 30px; width: 30px;" />
          {/if}
        </div>
        <div class="fizz-flex-item">
          <div class="fizz-stack-2 fizz-max-content">
            <UIRouterLink
              stateName="layout.product_listing"
              params={{ distributors: order.distributor.id }}
              classes="fizz-text-inherit fizz-text-strong"
              eventName="cart_distributor_catalog_redirect"
              trackingParams={[
                new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
              ]}
            >
              {order.distributor.name}
            </UIRouterLink>
          </div>
          {#if !hideAllDistributorInfo}
            {#if retailerUserAuthStatus == "valid"}
              {#await getStockSiteLocation(order.distributor.id, retailerAuth?.account_number) then data}
                <span class="fizz-text-small" data-testid="agency-store-info">
                  {data.name || ""}
                </span>
              {/await}
            {:else if order.sales_reps.length > 0 && !needsAccountNumber}
              <div class="fizz-stack-2">
                <UIRouterLink
                  stateName="layout.accounts"
                  classes="fizz-text-inherit fizz-text-small"
                  id="sales-rep-names"
                  style="display: block"
                  eventName="cart_sales_rep"
                  trackingParams={[
                    new CartOrderEntity({ distributorId: order.distributor.id, orderId: order.id }),
                    new SalesRepsEventEntity({
                      ids: orderSalesRepsIds(order),
                      service: "cart",
                      component: "cart_order_header",
                    }),
                  ]}
                >
                  {orderSalesRepsString(order)}
                </UIRouterLink>
              </div>
            {:else if !(order.distributor.reasons && order.distributor.reasons.rep) || needsAccountNumber}
              <div class="fizz-text-small fizz-stack-2">
                {emptyText}
              </div>
            {:else}
              <div class="fizz-text-small fizz-stack-2">
                {order.distributor.name} Sales
              </div>
            {/if}
            <div class="fizz-stack-2">
              <OrderRequirements
                cutoffTime={order.next_cutoff}
                distributorName={order.distributor.name}
                minPurchase={order.distributor.minimum_dollars_per_order}
                minQuantity={order.distributor.minimum_quantity_per_order}
                notes={order.distributor.ordering_notes}
                {isMobile}
                onModalOpen={onOrderReqModalOpen}
              />
            </div>
          {/if}
        </div>
      </div>
      {#if !isMobile}
        <Price {products} {total} {isMobile} />
        <div class="fizz-tooltip-container fizz-align-self-center">
          <button
            on:mousedown|preventDefault={onClickAccordion}
            on:mouseover={enterAccordionButton}
            on:mouseleave={leaveAccordionButton}
            class="fizz-tooltip-trigger fizz-icon-button"
            class:hover-accordion-button={hoveringForAccordionButton}
            data-testid="accordion-button-{accordionOpen ? 'open' : 'closed'}"
            id="individual-order-accordion"
          >
            <Icon type={accordionIcon} height="12" width="12" />
          </button>
          <div class="fizz-tooltip fizz-tooltip-top fizz-tooltip-center" role="tooltip">
            {tooltipText}
          </div>
        </div>
      {/if}
    </span>
  </section>
  {#if !order.distributor?.ordering_disabled}
    <OrderAlertsSection {order} {highlightErrors} {openConnectDistributorModal} {cartService} />
  {/if}
</div>
